import { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronDown, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AllSchoolRow } from '@/hooks/useAdminAnalytics';

// ── Types ─────────────────────────────────────────────────────────────────────

export type FilterField =
  | 'school_name'
  | 'admin_email'
  | 'plan_tier'
  | 'plan_sub_tier'
  | 'billing_status'
  | 'suspended'
  | 'stripe_customer_id'
  | 'stripe_subscription_id'
  | 'total_assessments'
  | 'assessments_this_month'
  | 'school_created_at'
  | 'last_assessment_at';

export type FilterOperator =
  | 'contains'
  | 'does_not_contain'
  | 'is'
  | 'is_not'
  | 'starts_with'
  | 'ends_with'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than'
  | 'on_or_after'
  | 'on_or_before';

export interface FilterCondition {
  id: string;
  field: FilterField;
  operator: FilterOperator;
  value: string;
}

export interface FilterGroup {
  id: string;
  conditions: FilterCondition[];
}

// ── Internal/test identification preset ───────────────────────────────────────
// Each group is one OR branch. Conditions within a group are AND-ed.
// Edit these patterns to match your own test/internal account identifiers.

type GroupTemplate = { conditions: Omit<FilterCondition, 'id'>[] };

export const IDENTIFY_INTERNAL_PRESET: GroupTemplate[] = [
  { conditions: [{ field: 'admin_email', operator: 'contains', value: 'pmodaley@gmail.com' }] },
  { conditions: [{ field: 'school_name', operator: 'contains', value: 'test' }] },
];

// ── Field / operator config ───────────────────────────────────────────────────

type ValueType = 'text' | 'number' | 'enum' | 'boolean' | 'date';

interface FieldConfig {
  label: string;
  valueType: ValueType;
  operators: FilterOperator[];
  options?: { value: string; label: string }[];
}

const FIELD_CONFIGS: Record<FilterField, FieldConfig> = {
  school_name: {
    label: 'School name',
    valueType: 'text',
    operators: ['contains', 'does_not_contain', 'is', 'is_not', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  },
  admin_email: {
    label: 'Admin email',
    valueType: 'text',
    operators: ['contains', 'does_not_contain', 'is', 'is_not', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  },
  plan_tier: {
    label: 'Plan tier',
    valueType: 'enum',
    operators: ['is', 'is_not'],
    options: [
      { value: 'free', label: 'Free' },
      { value: 'individual', label: 'Individual' },
      { value: 'school', label: 'School' },
    ],
  },
  plan_sub_tier: {
    label: 'Plan sub-tier',
    valueType: 'enum',
    operators: ['is', 'is_not', 'is_empty', 'is_not_empty'],
    options: [
      { value: 'starter', label: 'Starter' },
      { value: 'standard', label: 'Standard' },
      { value: 'plus', label: 'Plus' },
    ],
  },
  billing_status: {
    label: 'Billing status',
    valueType: 'enum',
    operators: ['is', 'is_not'],
    options: [
      { value: 'active', label: 'Active' },
      { value: 'trialing', label: 'Trialing' },
      { value: 'past_due', label: 'Past due' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'paused', label: 'Paused' },
    ],
  },
  suspended: {
    label: 'Suspended',
    valueType: 'boolean',
    operators: ['is'],
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  stripe_customer_id: {
    label: 'Stripe customer ID',
    valueType: 'text',
    operators: ['contains', 'does_not_contain', 'is', 'is_not', 'starts_with', 'is_empty', 'is_not_empty'],
  },
  stripe_subscription_id: {
    label: 'Stripe subscription ID',
    valueType: 'text',
    operators: ['contains', 'does_not_contain', 'is', 'is_not', 'starts_with', 'is_empty', 'is_not_empty'],
  },
  total_assessments: {
    label: 'Total assessments',
    valueType: 'number',
    operators: ['is', 'greater_than', 'less_than'],
  },
  assessments_this_month: {
    label: 'Assessments this month',
    valueType: 'number',
    operators: ['is', 'greater_than', 'less_than'],
  },
  school_created_at: {
    label: 'School joined',
    valueType: 'date',
    operators: ['on_or_after', 'on_or_before', 'is_empty', 'is_not_empty'],
  },
  last_assessment_at: {
    label: 'Last assessment',
    valueType: 'date',
    operators: ['on_or_after', 'on_or_before', 'is_empty', 'is_not_empty'],
  },
};

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  contains: 'contains',
  does_not_contain: 'does not contain',
  is: 'is',
  is_not: 'is not',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  greater_than: 'greater than',
  less_than: 'less than',
  on_or_after: 'on or after',
  on_or_before: 'on or before',
};

// ── Matching logic (exported for use in parent) ────────────────────────────────

function getFieldValue(school: AllSchoolRow, field: FilterField): string | number {
  switch (field) {
    case 'school_name':           return school.school_name ?? '';
    case 'admin_email':           return school.admin_email ?? '';
    case 'plan_tier':             return school.plan_tier ?? '';
    case 'plan_sub_tier':         return school.plan_sub_tier ?? '';
    case 'billing_status':        return school.billing_status ?? '';
    case 'suspended':             return school.suspended_at ? 'yes' : 'no';
    case 'stripe_customer_id':    return school.stripe_customer_id ?? '';
    case 'stripe_subscription_id': return school.stripe_subscription_id ?? '';
    case 'total_assessments':      return school.total_assessments;
    case 'assessments_this_month': return school.assessments_this_month;
    case 'school_created_at':      return school.school_created_at ?? '';
    case 'last_assessment_at':     return school.last_assessment_at ?? '';
    default: return '';
  }
}

function parseTerms(value: string): string[] {
  return value.split(',').map(t => t.trim()).filter(Boolean);
}

function matchesCondition(school: AllSchoolRow, cond: FilterCondition): boolean {
  const noValue = ['is_empty', 'is_not_empty'].includes(cond.operator);
  if (!noValue && cond.value === '') return true; // incomplete — skip

  const raw = getFieldValue(school, cond.field);
  const valStr = String(raw).toLowerCase();

  // Numeric, date, and no-value operators don't support comma lists
  switch (cond.operator) {
    case 'is_empty':     return !raw || String(raw) === '';
    case 'is_not_empty': return !!raw && String(raw) !== '';
    case 'greater_than': return Number(raw) > Number(cond.value);
    case 'less_than':    return Number(raw) < Number(cond.value);
    case 'on_or_after':  return !!raw && new Date(String(raw)) >= new Date(cond.value + 'T00:00:00.000Z');
    case 'on_or_before': return !!raw && new Date(String(raw)) <= new Date(cond.value + 'T23:59:59.999Z');
  }

  // Text operators: split on comma so each term is tested as an OR alternative.
  // Negating operators (does_not_contain, is_not) require ALL terms to not match.
  const terms = parseTerms(cond.value).map(t => t.toLowerCase());
  if (terms.length === 0) return true;

  switch (cond.operator) {
    case 'contains':          return terms.some(t => valStr.includes(t));
    case 'does_not_contain':  return terms.every(t => !valStr.includes(t));
    case 'is':                return terms.some(t => valStr === t);
    case 'is_not':            return terms.every(t => valStr !== t);
    case 'starts_with':       return terms.some(t => valStr.startsWith(t));
    case 'ends_with':         return terms.some(t => valStr.endsWith(t));
    default: return true;
  }
}

function isActiveCondition(cond: FilterCondition): boolean {
  if (!cond.field || !cond.operator) return false;
  const noValue = ['is_empty', 'is_not_empty'].includes(cond.operator);
  return noValue || cond.value !== '';
}

export function applyAdvancedGroups(
  schools: AllSchoolRow[],
  groups: FilterGroup[],
): AllSchoolRow[] {
  const activeGroups = groups.filter(g => g.conditions.some(isActiveCondition));
  if (activeGroups.length === 0) return schools;
  return schools.filter(school =>
    activeGroups.some(group =>
      group.conditions.filter(isActiveCondition).every(cond => matchesCondition(school, cond)),
    ),
  );
}

// ── Single condition row ──────────────────────────────────────────────────────

interface ConditionRowProps {
  condition: FilterCondition;
  onChange: (c: FilterCondition) => void;
  onRemove: () => void;
  index: number;
}

function ConditionRow({ condition, onChange, onRemove, index }: ConditionRowProps) {
  const config = FIELD_CONFIGS[condition.field];
  const noValue = ['is_empty', 'is_not_empty'].includes(condition.operator);
  const isEnumOrBool = config.valueType === 'enum' || config.valueType === 'boolean';
  const isDate = config.valueType === 'date';

  function handleFieldChange(field: string) {
    const newConfig = FIELD_CONFIGS[field as FilterField];
    onChange({ ...condition, field: field as FilterField, operator: newConfig.operators[0], value: '' });
  }

  function handleOperatorChange(op: string) {
    onChange({ ...condition, operator: op as FilterOperator, value: '' });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {index > 0 && (
        <span className="text-xs text-muted-foreground w-7 text-right shrink-0">AND</span>
      )}
      {index === 0 && <span className="w-7 shrink-0" />}

      {/* Field */}
      <Select value={condition.field} onValueChange={handleFieldChange}>
        <SelectTrigger className="h-8 w-48 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.entries(FIELD_CONFIGS) as [FilterField, FieldConfig][]).map(([k, v]) => (
            <SelectItem key={k} value={k}>{v.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator */}
      <Select value={condition.operator} onValueChange={handleOperatorChange}>
        <SelectTrigger className="h-8 w-40 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {config.operators.map(op => (
            <SelectItem key={op} value={op}>{OPERATOR_LABELS[op]}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value */}
      {!noValue && (
        isEnumOrBool ? (
          <Select value={condition.value} onValueChange={v => onChange({ ...condition, value: v })}>
            <SelectTrigger className="h-8 w-36 text-sm">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {config.options?.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : isDate ? (
          <Input
            type="date"
            value={condition.value}
            onChange={e => onChange({ ...condition, value: e.target.value })}
            className="h-8 w-40 text-sm"
          />
        ) : (
          <div className="relative">
            <Input
              type={config.valueType === 'number' ? 'number' : 'text'}
              value={condition.value}
              onChange={e => onChange({ ...condition, value: e.target.value })}
              className="h-8 w-52 text-sm"
              placeholder={config.valueType === 'number' ? '0' : 'Value…'}
            />
            {config.valueType === 'text' && (
              <p className="absolute top-full left-0 mt-0.5 text-xs text-muted-foreground whitespace-nowrap">
                Separate multiple items with a comma
              </p>
            )}
          </div>
        )
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ── Filter group block ─────────────────────────────────────────────────────────

interface GroupBlockProps {
  group: FilterGroup;
  groupIndex: number;
  isOnly: boolean;
  onChange: (g: FilterGroup) => void;
  onRemove: () => void;
}

function GroupBlock({ group, groupIndex, isOnly, onChange, onRemove }: GroupBlockProps) {
  function addCondition() {
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        { id: crypto.randomUUID(), field: 'school_name', operator: 'contains', value: '' },
      ],
    });
  }

  function updateCondition(id: string, updated: FilterCondition) {
    onChange({ ...group, conditions: group.conditions.map(c => c.id === id ? updated : c) });
  }

  function removeCondition(id: string) {
    const next = group.conditions.filter(c => c.id !== id);
    if (next.length === 0) {
      onRemove();
    } else {
      onChange({ ...group, conditions: next });
    }
  }

  return (
    <div className="rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        {groupIndex > 0 && (
          <span className="text-xs font-semibold text-primary uppercase tracking-wide">OR</span>
        )}
        {groupIndex === 0 && (
          <span className="text-xs text-muted-foreground">Group {groupIndex + 1}</span>
        )}
        {!isOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="h-6 px-1.5 text-xs text-muted-foreground hover:text-destructive ml-auto"
          >
            Remove group
          </Button>
        )}
      </div>

      {group.conditions.map((cond, i) => (
        <ConditionRow
          key={cond.id}
          condition={cond}
          index={i}
          onChange={updated => updateCondition(cond.id, updated)}
          onRemove={() => removeCondition(cond.id)}
        />
      ))}

      <div className="pl-9">
        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground px-2" onClick={addCondition}>
          <Plus className="h-3 w-3 mr-1" />
          Add condition
        </Button>
      </div>
    </div>
  );
}

// ── Panel component ────────────────────────────────────────────────────────────

interface Props {
  groups: FilterGroup[];
  onChange: (groups: FilterGroup[]) => void;
}

function totalConditions(groups: FilterGroup[]) {
  return groups.reduce((sum, g) => sum + g.conditions.length, 0);
}

export default function AnalyticsAdvancedFilters({ groups, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const prevCountRef = useRef(totalConditions(groups));

  // Auto-open when groups/conditions are added from outside (e.g. preset button)
  useEffect(() => {
    const count = totalConditions(groups);
    if (count > 0 && prevCountRef.current === 0) {
      setOpen(true);
    }
    prevCountRef.current = count;
  }, [groups]);

  function addGroup() {
    onChange([
      ...groups,
      {
        id: crypto.randomUUID(),
        conditions: [{ id: crypto.randomUUID(), field: 'school_name', operator: 'contains', value: '' }],
      },
    ]);
  }

  function updateGroup(id: string, updated: FilterGroup) {
    onChange(groups.map(g => g.id === id ? updated : g));
  }

  function removeGroup(id: string) {
    const next = groups.filter(g => g.id !== id);
    onChange(next);
    if (next.length === 0) setOpen(false);
  }

  function applyPreset() {
    const next: FilterGroup[] = IDENTIFY_INTERNAL_PRESET.map(template => ({
      id: crypto.randomUUID(),
      conditions: template.conditions.map(c => ({ ...c, id: crypto.randomUUID() })),
    }));
    onChange([...groups, ...next]);
  }

  const condCount = totalConditions(groups);
  const badgeCount = condCount > 0 ? condCount : null;

  return (
    <Card>
      <CardContent className="py-3">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 text-sm font-medium w-full text-left text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform shrink-0', open && 'rotate-180')} />
          <span>Advanced filters</span>
          {badgeCount !== null && (
            <Badge variant="secondary" className="ml-1">{badgeCount}</Badge>
          )}
        </button>

        {open && (
          <div className="mt-3 space-y-2">
            {groups.length === 0 && (
              <p className="text-xs text-muted-foreground pl-2">
                No filter groups set. Add one below or apply a preset.
              </p>
            )}

            {groups.map((group, i) => (
              <GroupBlock
                key={group.id}
                group={group}
                groupIndex={i}
                isOnly={groups.length === 1}
                onChange={updated => updateGroup(group.id, updated)}
                onRemove={() => removeGroup(group.id)}
              />
            ))}

            <div className="flex flex-wrap gap-2 pt-1 pl-1">
              {groups.length === 0 ? (
                <Button variant="outline" size="sm" className="h-8" onClick={addGroup}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add filter group
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="h-8" onClick={addGroup}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add OR group
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-8" onClick={applyPreset}>
                Identify test accounts
              </Button>
              {groups.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-muted-foreground"
                  onClick={() => { onChange([]); setOpen(false); }}
                >
                  Clear all
                </Button>
              )}
            </div>

            {groups.length > 1 && (
              <p className="text-xs text-muted-foreground pl-1">
                Groups combine with OR. Conditions within each group combine with AND.
              </p>
            )}
            {groups.length === 1 && groups[0].conditions.length > 1 && (
              <p className="text-xs text-muted-foreground pl-1">
                All conditions must match (AND logic). Add another group for OR logic.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
